import React, { useEffect } from "react";
import { ThemeProvider } from "styled-components";
import { BrowserRouter } from "react-router-dom";
import { Provider } from "react-redux";
import { setupListeners } from "@reduxjs/toolkit/query";
import { CookiesProvider } from "react-cookie";
import { I18nextProvider, initReactI18next } from "react-i18next";
import i18n from "i18next";

import App from "./App";

import translationGB from "i18n/gb/resource.json";
import translationES from "i18n/es/resource.json";
import translationDE from "i18n/de/resource.json";
import translationEG from "i18n/eg/resource.json";
import translationLW from "i18n/lw/resource.json";
import translationPT from "i18n/pt/resource.json";
import translationZA from "i18n/za/resource.json";
import translationRO from "i18n/ro/resource.json";
import translationCZ from "i18n/cz/resource.json";
import translationTR from "i18n/tr/resource.json";
import translationNL from "i18n/nl/resource.json";
import translationGR from "i18n/gr/resource.json";

import GlobalStyle from "themes/globalStyle";

import { store } from "state/reducers-combiner";
import theme from "themes/default";
import themeLowi from "themes/lowi";
import getOpco from "utils/getOpco";
import { initDebug } from "utils/debugHelper";
import useOpcoGatekeeper from "hooks/useOpcoGatekeeper";
import useDomainGatekeeper from "hooks/useDomainGatekeeper";
import { isFeatureActive } from "utils/getFeature";
import themeCZ from "themes/cz";
import CzStyle from "themes/cz/czStyle";

export const i18nInstance = i18n.createInstance();

i18nInstance
  .use(initReactI18next) // passes i18n down to react-i18next
  .init({
    // the translations
    // (tip move them in a JSON file and import them,
    // or even better, manage them via a UI: https://react.i18next.com/guides/multiple-translation-files#manage-your-translations-with-a-management-gui)
    lng: getOpco(),
    resources: {
      gb: {
        translation: translationGB,
      },
      es: {
        translation: translationES,
      },
      de: {
        translation: translationDE,
      },
      eg: {
        translation: translationEG,
      },
      lw: {
        translation: translationLW,
      },
      pt: {
        translation: translationPT,
      },
      za: {
        translation: translationZA,
      },
      ro: {
        translation: translationRO,
      },
      cz: {
        translation: translationCZ,
      },
      tr: {
        translation: translationTR,
      },
      nl: {
        translation: translationNL,
      },
      gr: {
        translation: translationGR,
      },
    },
    fallbackLng: "gb",
    saveMissing: true,
    interpolation: {
      escapeValue: false, // react already safes from xss => https://www.i18next.com/translation-function/interpolation#unescape
    },
    supportedLngs: ["gb", "es", "de", "eg", "lw", "pt", "za", "ro", "cz", "tr", "gr", "nl"],
  });

setupListeners(store.dispatch);

function AppWrapper() {
  useDomainGatekeeper();
  useOpcoGatekeeper();
  useEffect(() => {
    initDebug();
  }, []);

  const getActiveTheme = () => {
    if (isFeatureActive("Enabled default theme")) {
      return theme;
    } else if (isFeatureActive("Enabled Lowi theme")) {
      return themeLowi;
    } else if (isFeatureActive("Enabled CZ theme")) {
      return themeCZ;
    } else {
      return theme; // fallback to default theme
    }
  };

  const activeTheme = getActiveTheme();

  return (
    <React.StrictMode>
      <I18nextProvider i18n={i18nInstance}>
        <ThemeProvider theme={activeTheme}>
          {isFeatureActive("Enabled CZ theme") ? <CzStyle /> : <GlobalStyle />}
          <CookiesProvider>
            <Provider store={store}>
              <BrowserRouter basename={`/subscriptionplus/${getOpco()}`}>
                <App />
              </BrowserRouter>
            </Provider>
          </CookiesProvider>
        </ThemeProvider>
      </I18nextProvider>
    </React.StrictMode>
  );
}

export default AppWrapper;
